import { resources } from '@forto-dev/platform-localisation'

/**
 * Extracts all paths from an nested object.
 * @example
 * ```
 * Paths<{ a: string, b: string }> === 'a' | 'b'
 * Paths<{ a: string, nested: { b: string } }> === 'a' | 'nested.b'
 * ```
 */
type Paths<T, Path extends string = ''> =
  // End of the recursion - exit with the created paths { a: ->string<-, b: { c: ->string<- } }
  T extends string
    ? Path
    : // Collect all keys of the object { ->a<-: string }
      T extends { [index in infer Key]: unknown }
      ? // Guard against keys that cannot be understood by the TS compiler, such as symbols
        Key extends string
        ? // At each path check the value and recurse if needed { a: ->{ b: string }<-, c: ->string<- }
          Paths<T[Key], Path extends '' ? Key : `${Path}.${Key}`>
        : never
      : never

type Resources = (typeof resources)['en']
type NumberResources = Resources['numbers']

/**
 * Helper type to extract format keys by prefix. This type enables extensible format handling
 * by automatically capturing all format keys that match a given prefix pattern.
 *
 * @example Adding new number formats in the resource files:
 * {
 *   numbers: {
 *     format: {
 *       number: '...',           // Basic format
 *       number_short: '...',     // Shortened format (e.g., 1K, 1M)
 *       number_percent: '...',   // Percentage format
 *     }
 *   }
 * }
 *
 * @example Adding new currency formats in the resource files:
 * {
 *   numbers: {
 *     format: {
 *       currency: '...',           // Standard currency format
 *       currency_name: '...',      // Full currency name
 *       currency_accounting: '...', // Accounting format with parentheses
 *     }
 *   }
 * }
 *
 * The type will automatically pick up any new formats as long as they:
 * 1. Are added under the 'format' namespace
 * 2. Follow the pattern 'format.<prefix><any_suffix>'
 * 3. Exist in the resources
 *
 * @template T - The resource type containing format definitions
 * @template Prefix - The prefix to match ('number' or 'currency')
 */
type ExtractFormatKeys<T, Prefix extends string> = Extract<Paths<T>, `format.${Prefix}${string}`>

export type DateTranslationKey = keyof Resources['dates']
export type FormTranslationKey = keyof Resources['form']
export type CommonTranslationKey = Paths<Resources['common']>

// Get all format keys for numbers and currency
export type NumberFormatKey = ExtractFormatKeys<NumberResources, 'number'>
export type CurrencyFormatKey = ExtractFormatKeys<NumberResources, 'currency'>

export const isValidDateTranslationKey = (key: string | undefined): key is DateTranslationKey =>
  Boolean(key && key in resources.en.dates)

export const isValidFormTranslationKey = (key: string | undefined): key is FormTranslationKey =>
  Boolean(key && key in resources.en.form)

// Type guard functions with prefix matching
export const isValidNumberFormatKey = (key: string | undefined): key is NumberFormatKey =>
  Boolean(key && key.startsWith('format.number') && key in resources.en.numbers)

export const isValidCurrencyFormatKey = (key: string | undefined): key is CurrencyFormatKey =>
  Boolean(key && key.startsWith('format.currency') && key in resources.en.numbers)
